// src/lib/ratioMetrics.ts (2026-06-30 新設)
// 比指標(単価・率・平均在院日数 等)の「分子/分母」単一真実源。
//
// 【なぜ要るか】比指標を期間集計(直近12ヶ月平均/年度累計/着地予測)する時、
//   月別の比を単純平均すると volume 加重が消えて誤る(入院単価で病院全体 −3.3%、
//   変動の大きい小規模科で最大 −4.6%)。正しい期間値は Σ分子 / Σ分母。
//   build_dashboard_db.R は科ロールアップ(小→中/大)を既に加重再導出している
//   (「weighted-average で小単価の単純平均より正確」)。時間方向も同じ規則に揃える。
//
// 【正本】各式は build_dashboard_db.R の派生ロジックと一致させること。
//   - 単価系 = 収入(千円) × 1000 / 延数(人日)  → 円/日
//   - 平均在院日数 = 在院患者延数 / ((新規入院数+他科から転科+他科への転科+退院患者数)/2)
//   - DPCⅡ以内率 = 期間2以内延数 / (期間2以内 + 期間2超)
//
// period値 = (scale × Σ_months Σ_num) / ((Σ_months Σ_den) / (denDiv ?? 1))
//   num/den は加算指標の配列(月をまたいで Σ)。全て線形なので Σ(a/2)=(Σa)/2 が成立。

export interface RatioSpec {
  num: string[];        // 分子を成す加算指標(合算)
  den: string[];        // 分母を成す加算指標(合算)
  denDiv?: number;      // 分母合計をこれで割る(平均在院日数の /2 等)
  scale?: number;       // 比に掛ける(単価の ×1000、率は省略=1)
  unitDays?: boolean;   // 分母が「暦日数」= データ指標でなく期間の日数(入院患者延数(1日平均))
  needsData?: string;   // まだ fact_long_all に分母が無い場合の不足指標名(稼働率/利用率)
}

export const RATIO_METRICS: Record<string, RatioSpec> = {
  // ── 単価系(円/日)= 収入(千円)×1000 / 延数(人日) ──
  "入院単価":               { num: ["入院収入"],               den: ["入院患者延数"], scale: 1000 },
  "在院単価":               { num: ["入院収入"],               den: ["在院患者延数"], scale: 1000 },
  "GP単価":                 { num: ["GP"],                     den: ["入院患者延数"], scale: 1000 },
  "GP単価_在院":            { num: ["GP"],                     den: ["在院患者延数"], scale: 1000 },
  "入院単価(DPC期間2以内)": { num: ["入院収入(DPC期間2以内)"], den: ["入院患者延数(DPC期間2以内)"], scale: 1000 },
  "入院単価(DPC期間2超)":   { num: ["入院収入(DPC期間2超)"],   den: ["入院患者延数(DPC期間2超)"],   scale: 1000 },
  "入院単価(出来高)":       { num: ["入院収入(出来高)"],       den: ["入院患者延数(出来高)"],       scale: 1000 },
  "在院単価(DPC期間2以内)": { num: ["入院収入(DPC期間2以内)"], den: ["在院患者延数(DPC期間2以内)"], scale: 1000 },
  "在院単価(DPC期間2超)":   { num: ["入院収入(DPC期間2超)"],   den: ["在院患者延数(DPC期間2超)"],   scale: 1000 },
  "医師平均GP":             { num: ["GP"],                     den: ["実態常勤換算数"] },

  // ── DPC期間 率(日数ベース)= 期間2以内 / (以内 + 超) ──
  "DPC期間2以内率":   { num: ["入院患者延数(DPC期間2以内)"], den: ["入院患者延数(DPC期間2以内)", "入院患者延数(DPC期間2超)"] },
  "在院DPC期間2以内率": { num: ["在院患者延数(DPC期間2以内)"], den: ["在院患者延数(DPC期間2以内)", "在院患者延数(DPC期間2超)"] },
  // 2026-07-17: den を 入院患者延数 → 三者和 に統一。
  //   R 側 (aggregation/script/30_実績集計/lib/_比指標.R の RATIO_DEFS) は三者和で、
  //   ここだけ 入院患者延数 だったため **単月表示(R由来) と 期間集計(TS由来) で
  //   同じ指標の定義が食い違っていた**。実測では三者和 == 入院患者延数 が
  //   全 13,908 cohort で一致する (DPC期間 が NA の行が実在しない) ため現状は同値だが、
  //   将来 DPC期間 NA が出ると乖離する。分子 ⊆ 分母 が成立する三者和が率の定義として正しい。
  "DPC外率":          { num: ["入院患者延数(出来高)"], den: ["入院患者延数(DPC期間2以内)", "入院患者延数(DPC期間2超)", "入院患者延数(出来高)"] },
  // 患者ベース = 退院数ベース
  "DPC期間2以内率(患者ベース)": { num: ["DPC期間2以内退院数"], den: ["DPC期間2以内退院数", "DPC期間2超退院数"] },
  "DPC外率(患者ベース)":        { num: ["出来高退院数"],       den: ["DPC期間2以内退院数", "DPC期間2超退院数", "出来高退院数"] },

  // ── 平均在院日数 = 在院患者延数 / ((新規入院数 + 他科から転科 + 他科への転科 + 退院患者数)/2) ──
  "平均在院日数": {
    num: ["在院患者延数"],
    den: ["新規入院数", "他科から転科", "他科への転科", "退院患者数"],
    denDiv: 2,
  },

  // ── 入院率系(件数 / 件数) ──
  "紹介入院率": { num: ["紹介入院数"], den: ["新規入院数"] },
  "予定入院率": { num: ["予定入院数"], den: ["新規入院数"] },

  // ── 1日平均(分母=暦日数。期間の Σ日数で割る) ──
  "入院患者延数(1日平均)": { num: ["入院患者延数"], den: [], unitDays: true },

  // ── 病床稼働率/利用率(分母=延病床数。集計コアで emit 済だが、リビルドで fact_long_all に載るまで
  //    needsData を残し mean フォールバック。リビルド後に needsData を外せば加重が有効化される) ──
  //    稼働率=入院患者延数(在院+退院)/延病床数、利用率=在院患者延数/延病床数 (集計コアの定義に一致)
  "病床稼働率": { num: ["入院患者延数"], den: ["延病床数"], needsData: "延病床数" },
  "病床利用率": { num: ["在院患者延数"], den: ["延病床数"], needsData: "延病床数" },
};

/** その指標が比(加重必須)か。true なら期間集計で月別単純平均してはいけない。 */
export function isRatioMetric(metric: string): boolean {
  return metric in RATIO_METRICS;
}

/**
 * 比指標の期間値を Σ分子/Σ分母 で算出。
 * @param spec       RATIO_METRICS[metric]
 * @param sumOf      指標名 → 期間内の Σ実績 を返す関数(呼び出し側が月次系列から合算)
 * @param daysInPeriod unitDays=true の比で使う、期間内の暦日数合計
 * @returns 加重平均した比、計算不能なら null
 */
export function weightedRatio(
  spec: RatioSpec,
  sumOf: (metric: string) => number | null,
  daysInPeriod?: number,
): number | null {
  const sumList = (ms: string[]): number | null => {
    let acc = 0;
    for (const m of ms) {
      const v = sumOf(m);
      if (v == null) return null;
      acc += v;
    }
    return acc;
  };
  const numSum = sumList(spec.num);
  if (numSum == null) return null;
  let denSum: number | null;
  if (spec.unitDays) {
    denSum = daysInPeriod ?? null;
  } else {
    denSum = sumList(spec.den);
    if (denSum != null && spec.denDiv) denSum = denSum / spec.denDiv;
  }
  if (denSum == null || denSum === 0) return null;
  return ((spec.scale ?? 1) * numSum) / denSum;
}
